import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDrawerStore } from '@/stores/drawerStore';
import { ApiError } from '@/services/apiClient';
import { DIAGNOSTIC_FORM_SCHEMA } from './schema';
import { buildDiagnosticPayload } from './lib/buildDiagnosticPayload';
import { useProposalMutation } from './hooks/useProposalMutation';
import { StepIndicator } from '@/components/common/form/StepIndicator';
import { StepRenderer } from '@/components/common/form/StepRenderer';
import { isStepValid } from '@/components/common/form/StepRenderer/validation';

const schema = DIAGNOSTIC_FORM_SCHEMA;
const totalSteps = schema.steps.length;

/**
 * DiagnosticFormView
 * 診断フォーム（マルチステップ）
 * スキーマ駆動: DIAGNOSTIC_FORM_SCHEMA から質問を取得しレンダリング
 * 最終ステップ完了時に diagnosticAnswers + formData をバックエンドAPIに送信
 */
export function DiagnosticFormView() {
  const [currentStep, setCurrentStep] = useState(0);
  const diagnosticAnswers = useDrawerStore((state) => state.diagnosticAnswers);
  const setAnswer = useDrawerStore((state) => state.setAnswer);
  const setActiveView = useDrawerStore((state) => state.setActiveView);
  const formData = useDrawerStore((state) => state.formData);

  const proposalMutation = useProposalMutation({
    onSuccess: () => setActiveView('completed'),
  });

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // 最終ステップ: diagnosticAnswers + formData をバックエンドAPIに送信
      const payload = buildDiagnosticPayload(schema, diagnosticAnswers);
      proposalMutation.mutate({
        websiteUrl: formData.websiteUrl,
        userCompanyName: formData.companyName,
        userContactName: formData.contactName,
        userEmail: formData.email,
        diagnosticData: JSON.stringify(payload),
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      // 最初のステップから顧客情報入力画面に戻る
      setActiveView('form');
    }
  };

  const currentStepDef = schema.steps[currentStep];
  const stepValid = isStepValid(schema, currentStep, diagnosticAnswers);
  const isLastStep = currentStep === totalSteps - 1;
  const errorMessage = proposalMutation.error
    ? proposalMutation.error instanceof ApiError
      ? proposalMutation.error.message
      : '提案の送信に失敗しました。もう一度お試しください。'
    : null;

  return (
    <>
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />

          {/* エラー表示 */}
          {errorMessage && (
            <div
              className="bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg mb-4"
              role="alert"
            >
              <p className="text-xs sm:text-sm font-medium">{errorMessage}</p>
            </div>
          )}

          {/* 質問のレンダリング */}
          {currentStepDef && (
            <StepRenderer
              questions={currentStepDef.questions}
              answers={diagnosticAnswers}
              setAnswer={setAnswer}
            />
          )}
        </div>
      </div>

      {/* Fixed Footer with Navigation Buttons */}
      <div className="border-t border-[#E5E7EB] bg-[#F9FAFB] p-4 sm:p-6 flex gap-3">
        <Button
          onClick={handleBack}
          disabled={proposalMutation.isPending}
          className="flex-1 bg-white hover:bg-[#F3F4F6] text-[#111827] border-2 border-[#E5E7EB] rounded-lg transition-all font-bold h-11 sm:h-12"
        >
          戻る
        </Button>
        <Button
          onClick={handleNext}
          disabled={!stepValid || proposalMutation.isPending}
          className="flex-1 bg-[#0052FF] hover:bg-[#0041CC] text-white rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-bold h-11 sm:h-12"
        >
          {isLastStep
            ? proposalMutation.isPending
              ? '送信中...'
              : '提案資料を受け取る'
            : '次へ'}
        </Button>
      </div>
    </>
  );
}
